import React, { useState, useEffect, ChangeEvent, FormEvent, useRef, useMemo } from 'react';
import axios, { AxiosResponse, AxiosError } from 'axios';
import './FileUploader.css';
import { useApiClient } from '../components/useApiClient';
import { createApiClient } from "./useApiClient";
import { useSafeRequest } from "./useSafeRequest";
import { useUser } from "./UserContext";
import useAuthToken from '../functions/useAuthToken';
import DocumentIconGallery from "./DocumentIconGallery";
import { Typography, Divider, Paper } from "@mui/material";
import { DocumentDetails } from "../models/Document";

import { ThemeProvider } from "@mui/material/styles";
import { theme } from "../styles/mui_theme";

// Define interfaces for types
export interface FileMetadata {
    title: string;
    description: string;
    documentTypeId: string;
    artworkId: string;
}

export interface Document {
    blobName: string;
    url: string;
    metadata: FileMetadata;
}
export interface FileUploaderProps {
    apiUrl: string;
    artworkId?: string;
    acceptedFileTypes?: string;
    onUploadSuccess?: (data: { message: string; results: Array<{ blobName: string; url: string; metadata: FileMetadata }> }) => void;
    onUploadError?: (error: string) => void;
    uploadResult?: (result: boolean, message: string) => void;
    showDeleteButton: boolean | null;
    viewOnly: boolean | null;
}

export interface UploadResponse {
    message: string;
    results: Document[];
}

export interface DocumentType {
    documentTypeId: number;
    name: string;
    code: string;
    category: string;
    sortOrder: number;
};

interface ListResponse {
    documents: DocumentDetails[];
}

type UploadResult = { success: boolean; count: number };

const FileUploader: React.FC<FileUploaderProps> = ({
    apiUrl,
    artworkId,
    acceptedFileTypes = '.jpg,.png,.pdf',
    onUploadSuccess,
    onUploadError,
    uploadResult,
    showDeleteButton,
    viewOnly
}) => {

    const [files, setFiles] = useState<File[]>([]);
    const [metadata, setMetadata] = useState<FileMetadata[]>([]);
    const [message, setMessage] = useState<string>('');
    const [documents, setDocuments] = useState<DocumentDetails[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);

    const { activeClientId, getAccessToken } = useUser();

    const api = React.useMemo(
        () =>
            createApiClient(
                {
                    getAccessToken,
                    getClientId: () => activeClientId ?? null,
                },
                { timeoutMs: 12000 }
            ),
        [getAccessToken, activeClientId]
    );

    const typesReq = useSafeRequest<any[]>(api, { maxAttempts: 3 });

    const loadAll = async () => {
        const tasks = [
            typesReq.run({ method: "GET", url: 'documents/types' }),

            //Other data requirements...
        ];

        const [a] = await Promise.allSettled(tasks);

        if (a.status === "fulfilled") setDocumentTypes(a.value);
    };

    const groupedDocTypes = useMemo(() => {
        const sortedDocTypes = [...documentTypes].sort((a, b) => a.sortOrder - b.sortOrder);
        const map: Record<string, typeof sortedDocTypes> = {};
        for (const s of sortedDocTypes) {
            (map[s.category] ||= []).push(s);
        }
        return map;
    }, [documentTypes]);


    // Fetch existing documents on mount or when artworkId changes
    const fetchDocuments = async () => {
        if (!artworkId) return;
        setIsLoading(true);
        setError('');
        try {

            const refData = await loadAll().catch(() => { });

            const accessToken = await getAccessToken();

            const response: AxiosResponse<ListResponse> = await axios.get(`${apiUrl}/list/${artworkId}`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    'X-Client-Id': activeClientId?.toString() || '',
                    "Content-Type": "multipart/form-data"
                }
            });

            setDocuments(response.data.documents || []);

        } catch (err) {
            const errorMessage = 'Failed to load documents';
            setError(errorMessage);
            onUploadError?.(errorMessage);
            console.error(err as AxiosError);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchDocuments();
    }, [apiUrl, artworkId, onUploadError]);

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>): void => {
        const selectedFiles = e.target.files ? Array.from(e.target.files) : [];
        setFiles(selectedFiles);
        setMetadata(selectedFiles.map(() => ({ title: '', description: '', artworkId: '', documentTypeId: '' })));
        setMessage('');
    };

    const handleMetadataChange = (index: number, field: keyof FileMetadata, value: string): void => {
        const newMetadata = [...metadata];
        newMetadata[index] = { ...newMetadata[index], [field]: value };
        setMetadata(newMetadata);
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();
        if (files.length === 0) {
            const errorMessage = 'Please select at least one file';
            setMessage(errorMessage);
            onUploadError?.(errorMessage);
            return;
        }

        if (!artworkId) {
            const errorMessage = 'Artwork ID is required';
            setMessage(errorMessage);
            onUploadError?.(errorMessage);
            return;
        }

        setIsLoading(true);
        setError('');

        const formData = new FormData();
        const metaStr = JSON.stringify(metadata);

        files.forEach((file) => formData.append('files', file));
        formData.append('metadata', metaStr);
        formData.append('artworkId', artworkId ?? ''); 

        const accessToken = await getAccessToken();

        try {
            

            const response: AxiosResponse<UploadResponse> = await axios.post(apiUrl, formData, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    'X-Client-Id': activeClientId?.toString() || '',
                    "Content-Type": "multipart/form-data"
                }
            });

            //const response = await uploadReq.run(cfg);

            setMessage('Files uploaded successfully!');
            setFiles([]);
            setMetadata([]);
            onUploadSuccess?.(response.data);
            uploadResult?.(true, 'Files uploaded successfully!');
            fetchDocuments();
        } catch (error) {
            const errorMessage = 'Error uploading files';
            setMessage(errorMessage);
            onUploadError?.(errorMessage);
            console.error(error as AxiosError);
        }
        finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async (docGuids: string[]): Promise<void> => {
        setIsLoading(true);
        setError('');
        try {
            console.info("Delete docs:", docGuids);

            for (var docGuid of docGuids) {
                console.info("Delete docs:", docGuid);
                
                await api.delete(`${apiUrl}/${artworkId}/${docGuid}`);
                setDocuments(documents.filter((doc) => doc.id !== docGuid));
            }

            setMessage('Documents deleted successfully');
        } catch (err) {
            const errorMessage = 'Failed to delete document';
            setError(errorMessage);
            onUploadError?.(errorMessage);
            console.error(err as AxiosError);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownload = (url: string): void => {
        window.open(url, '_blank', 'noopener,noreferrer');

    };

    return (
        <ThemeProvider theme={theme}>
        <Paper elevation={3} square={false} sx={{ p: 1, width: '100%', boxSizing: 'border-box' }}>
            <div style={{ padding: '0.5rem' }}>
                <div className="section-header">
                    <Typography variant="h6">Documents</Typography>
                </div>
                <Divider sx={{ my: 2 }} />

                <div className="file-uploader">
                    {!viewOnly &&
                        <form onSubmit={handleSubmit}>
                            <div className="file-input">
                                <label>Upload New Files:</label>
                                <input
                                    type="file"
                                    multiple
                                    accept={acceptedFileTypes}
                                    onChange={handleFileChange}
                                    disabled={isLoading}
                                />
                            </div>
                            {files.map((file, index) => (
                                <div key={index} className="file-metadata">
                                    <h5>File: {file.name}</h5>

                                    <label className="form-label">Document Type</label>
                                    <select
                                        value={metadata[index]?.documentTypeId || ''}
                                        disabled={isLoading}
                                        onChange={(e) =>
                                            handleMetadataChange(index, 'documentTypeId', e.target.value)
                                        }
                                        className="status-select"
                                    >
                                        <option value="">Select documemnt type</option>
                                        {Object.entries(groupedDocTypes).map(([category, items]) => (
                                            <optgroup key={category} label={category}>
                                                {items.map((s) => (
                                                    <option key={s.documentTypeId} value={s.documentTypeId}>
                                                        {s.name}
                                                    </option>
                                                ))}
                                            </optgroup>
                                        ))}
                                    </select>

                                    <label>
                                        Name:
                                        <input
                                            type="text"
                                            value={metadata[index]?.title || ''}
                                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                handleMetadataChange(index, 'title', e.target.value)
                                            }
                                            required
                                            disabled={isLoading}
                                            placeholder="Document name"
                                        />
                                    </label>
                                    <label>
                                        Description:
                                        <textarea
                                            value={metadata[index]?.description || ''}
                                            onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                                                handleMetadataChange(index, 'description', e.target.value)
                                            }
                                            disabled={isLoading}
                                            placeholder="Brief document description (optional)"
                                        />
                                    </label>
                                </div>
                            ))}
                            <button type="submit" disabled={!files || files.length === 0 || !artworkId || isLoading}>
                                {isLoading ? 'Uploading...' : 'Upload'}
                            </button>
                        </form>
                    }
                        {message && <p className={`message ${error ? 'error' : ''}`}>{message}</p>}

                        <Divider sx={{ my: 2 }} />
                    <Typography variant="h6">Existing Documents</Typography>
                    {isLoading && <p>Loading documents...</p>}
                    {error && <p className="error">{error}</p>}
                    {documents.length === 0 && !isLoading && <p>No documents found for this artwork.</p>}

                    <DocumentIconGallery
                        showDeleteButton={showDeleteButton}
                        documents={documents}
                            dense={false }
                        onDeleteSelected={async (ids) => {
                            handleDelete(ids)
                        }}
                    />
                </div>
            </div>
            </Paper>
        </ThemeProvider>
    );
};

export default FileUploader;